import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
  PaginationItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import * as XLSX from "xlsx";
import { REFUND_PAYMENT, SYNC_REFUND_STATUS } from "../graphql/mutation";
import CustomSnackbar from "./Snackbar";
import AppDatePicker from "./AppDatePicker";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import { useMutation, useQuery } from "@apollo/client";
import themestyle from "../theme";
import { GET_BILLING_SUMMARY } from "../graphql/query";
import { DOWNLOAD_INVOICE } from "../graphql/mutation";
import TruncatedTitle from "./TruncatedTitle";

type BillingExportOption =
  | "transactions"
  | "pass_purchases"
  | "offer_usage"
  | "event_ticket_sales"
  | "all";

const BILLING_EXPORT_OPTIONS: Array<{ value: BillingExportOption; title: string }> = [
  { value: "all", title: "All Details" },
  { value: "transactions", title: "Recent Transactions" },
  { value: "pass_purchases", title: "Pass Purchases" },
  { value: "offer_usage", title: "Offer Usage" },
  { value: "event_ticket_sales", title: "Event Ticket Sales" },
];

const INDIVIDUAL_BILLING_EXPORT_OPTIONS: BillingExportOption[] = [
  "transactions",
  "pass_purchases",
  "offer_usage",
  "event_ticket_sales",
];


function statusChip(status: string) {
  if (status === "Paid" || status === "SUCCESS" || status === "ACTIVE") return { dot: themestyle.colors.success, bg: "#DFF7EA", fg: "#2e7d32", label: "Paid" };
  if (status === "Pending" || status === "PENDING") return { dot: "#F59E0B", bg: "#FFF7ED", fg: "#B45309", label: "Pending" };
  if (status === "Refund Pending") return { dot: "#F59E0B", bg: "#FFF7ED", fg: "#B45309", label: "Refund Pending" };
  if (status === "Refunded") return { dot: "#2563EB", bg: "#DBEAFE", fg: "#1D4ED8", label: "Refunded" };
  if (status === "Refund Failed") return { dot: "#EF4444", bg: "#FEF2F2", fg: "#B91C1C", label: "Refund Failed" };
  return { dot: "#EF4444", bg: "#FEE2E2", fg: "#B91C1C", label: status };
}

const formatDate = (dateStr: string) => {
  if (!dateStr || !dateStr.includes("-")) return dateStr;
  return dateStr.split("-").reverse().join("-");
};

function CardShell({ children, sx }: { children: React.ReactNode; sx?: any }) {
  return (
    <Card
      elevation={0}
      sx={{
        ...panelSx,
        ...sx,
      }}
    >
      {children}
    </Card>
  );
}

function formatDateDDMMYYYY(dateInput?: string | Date | null) {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

function formatCurrencyTwoDecimals(value?: number | null) {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-IN", {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function formatUsageLabel(value?: number | null) {
  const uses = Number(value || 0);
  return `${uses} used`;
}

export default function BillingDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [range, setRange] = useState<"today" | "week" | "month" | "year" | "specific-day" | "specific-month" | "specific-year">("month");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [filter, setFilter] = useState<"all" | "pass" | "event">("all");
  const [rangeAnchorEl, setRangeAnchorEl] = useState<null | HTMLElement>(null);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedExportOptions, setSelectedExportOptions] = useState<BillingExportOption[]>([]);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);
  const [refundReason, setRefundReason] = useState("");
  const [syncingPaymentId, setSyncingPaymentId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error" | "info">("success");

  const { data, loading, error, refetch } = useQuery(GET_BILLING_SUMMARY);
  const [refundPayment, { loading: refunding }] = useMutation(REFUND_PAYMENT);
  const [syncRefundStatus] = useMutation(SYNC_REFUND_STATUS);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [passPurchasesPage, setPassPurchasesPage] = useState(1);
  const [offerUsagePage, setOfferUsagePage] = useState(1);
  const [eventSalesPage, setEventSalesPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const defaultDate = useMemo(() => new Date().toISOString().split("T")[0], []);
  const defaultMonth = useMemo(() => new Date().toISOString().slice(0, 7), []);
  const defaultYear = useMemo(() => new Date().getFullYear().toString(), []);
  const canResetBillingFilters =
    range !== "month" ||
    selectedDate !== defaultDate ||
    selectedMonth !== defaultMonth ||
    selectedYear !== defaultYear ||
    filter !== "all";
  
  const TRANSACTIONS_PER_PAGE = 5;
  const PASS_PURCHASES_PER_PAGE = 5;
  const OFFER_USAGE_PER_PAGE = 5;
  const EVENT_SALES_PER_PAGE = 5;
  
  const handleClearBillingFilters = () => {
    setRange("month");
    setSelectedDate(defaultDate);
    setSelectedMonth(defaultMonth);
    setSelectedYear(defaultYear);
    setFilter("all");
    setTransactionsPage(1);
    setPassPurchasesPage(1);
    setOfferUsagePage(1);
    setEventSalesPage(1);
  };

  const ResponsivePagination = ({ 
    page, 
    count, 
    onChange 
  }: { 
    page: number; 
    count: number; 
    onChange: (page: number) => void 
  }) => {
    if (isMobile) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            size="small"
            disabled={page <= 1}
            onClick={() => onChange(page - 1)}
            sx={{ 
              border: "1px solid #E5E7EB", 
              bgcolor: "#fff",
              "&:hover": { bgcolor: "#F9FAFB" },
              width: 32,
              height: 32
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={page >= count}
            onClick={() => onChange(page + 1)}
            sx={{ 
              border: "1px solid #E5E7EB", 
              bgcolor: "#fff",
              "&:hover": { bgcolor: "#F9FAFB" },
              width: 32,
              height: 32
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
      );
    }

    return (
      <Pagination
        count={count}
        page={page}
        onChange={(_, val) => onChange(val)}
        size="small"
        siblingCount={0}
        boundaryCount={1}
        renderItem={(item) => (
          <PaginationItem
            {...item}
            sx={{
              mx: 0.25,
              fontWeight: 700,
              color: "#6B7280",
              "&.Mui-selected": {
                bgcolor: "#a3d65c",
                color: "#fff",
                "&:hover": { bgcolor: "#86b347" },
              },
              borderRadius: "50%",
              width: 32,
              height: 32,
            }}
          />
        )}
      />
    );
  };

  const [downloadInvoice] = useMutation(DOWNLOAD_INVOICE);

  const handleDownloadInvoice = async (paymentId: string, orderId: string) => {
    try {
      setDownloadingId(paymentId);
      const { data } = await downloadInvoice({
        variables: { paymentId },
      });

      const result = data?.downloadInvoice;
      if (!result?.success || !result.contentBase64) {
        throw new Error(result?.message || "Failed to download invoice");
      }

      const byteCharacters = window.atob(result.contentBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: result.mimeType || "application/pdf",
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = result.filename || `Invoice_${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Error downloading invoice:", err);
      // You could add a snackbar here if desired
    } finally {
      setDownloadingId(null);
    }
  };

  const billingData = data?.getBillingSummary?.data;

  const STATS = [
    { 
      label: "Total Revenue", 
      value: `₹${billingData?.stats?.totalRevenue?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || 0}`,
      icon: <AccountBalanceWalletOutlinedIcon />,
      color: "#3B82F6",
      bg: "#EFF6FF"
    },
    { 
      label: "Passes Purchased", 
      value: `${billingData?.stats?.passPurchasesCount || 0}`,
      icon: <ConfirmationNumberOutlinedIcon />,
      color: "#A3D65C",
      bg: "#F7FAF3"
    },
    { 
      label: "Offers Used", 
      value: `${billingData?.stats?.offersUsedCount || 0}`,
      icon: <PercentIcon />,
      color: "#F59E0B",
      bg: "#FFFBEB"
    },
    { 
      label: "Event Tickets Sold", 
      value: `${billingData?.stats?.eventTicketsSold || 0}`,
      icon: <EventAvailableOutlinedIcon />,
      color: "#8B5CF6",
      bg: "#F5F3FF"
    },
  ];

  const getDateKey = (value: Date | string) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const filteredTransactions = useMemo(() => {
    const tx = billingData?.recentTransactions || [];
    return tx.filter((t: any) => {
      const matchesType = filter === "all" || t.itemType?.toLowerCase() === filter.toLowerCase();
      if (!t.date) return false;

      const date = new Date(t.date);
      if (Number.isNaN(date.getTime())) return false;

      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const selectedStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const selectedKey = getDateKey(t.date);
      const todayKey = getDateKey(startOfToday);
      
      let matchesRange = false;
      if (range === "specific-day") {
        matchesRange = selectedKey === selectedDate;
      } else if (range === "specific-month") {
        matchesRange = selectedKey.startsWith(selectedMonth);
      } else if (range === "specific-year") {
        matchesRange = selectedKey.startsWith(selectedYear);
      } else {
        matchesRange =
          range === "today"
            ? selectedKey === todayKey
            : Math.floor((startOfToday.getTime() - selectedStart.getTime()) / 86400000) >= 0 &&
              Math.floor((startOfToday.getTime() - selectedStart.getTime()) / 86400000) <
                (range === "week" ? 7 : range === "month" ? 30 : 365);
      }

      return matchesType && matchesRange;
    });
  }, [billingData, filter, range, selectedDate, selectedMonth, selectedYear]);

  const passPurchases = billingData?.recentPassPurchases || [];
  const offerUsage = billingData?.offerUsage || [];
  const eventTicketSales = billingData?.eventTicketSales || [];

  useEffect(() => {
    setTransactionsPage(1);
  }, [filter, range, filteredTransactions.length]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(passPurchases.length / PASS_PURCHASES_PER_PAGE));
    if (passPurchasesPage > maxPage) setPassPurchasesPage(maxPage);
  }, [passPurchases.length, passPurchasesPage]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(offerUsage.length / OFFER_USAGE_PER_PAGE));
    if (offerUsagePage > maxPage) setOfferUsagePage(maxPage);
  }, [offerUsage.length, offerUsagePage]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(eventTicketSales.length / EVENT_SALES_PER_PAGE));
    if (eventSalesPage > maxPage) setEventSalesPage(maxPage);
  }, [eventTicketSales.length, eventSalesPage]);

  const paginatedTransactions = filteredTransactions.slice(
    (transactionsPage - 1) * TRANSACTIONS_PER_PAGE,
    transactionsPage * TRANSACTIONS_PER_PAGE
  );
  const paginatedPassPurchases = passPurchases.slice(
    (passPurchasesPage - 1) * PASS_PURCHASES_PER_PAGE,
    passPurchasesPage * PASS_PURCHASES_PER_PAGE
  );
  const paginatedOfferUsage = offerUsage.slice(
    (offerUsagePage - 1) * OFFER_USAGE_PER_PAGE,
    offerUsagePage * OFFER_USAGE_PER_PAGE
  );
  const paginatedEventSales = eventTicketSales.slice(
    (eventSalesPage - 1) * EVENT_SALES_PER_PAGE,
    eventSalesPage * EVENT_SALES_PER_PAGE
  );

  const handleToggleExportOption = (option: BillingExportOption) => {
    if (option === "all") {
      setSelectedExportOptions((current) =>
        current.includes("all") ? [] : ["all", ...INDIVIDUAL_BILLING_EXPORT_OPTIONS],
      );
      return;
    }

    setSelectedExportOptions((current) => {
      const baseOptions = current.filter((value) => value !== "all");
      const nextOptions = baseOptions.includes(option)
        ? baseOptions.filter((value) => value !== option)
        : [...baseOptions, option];

      if (nextOptions.length === INDIVIDUAL_BILLING_EXPORT_OPTIONS.length) {
        return ["all", ...INDIVIDUAL_BILLING_EXPORT_OPTIONS];
      }

      return nextOptions;
    });
  };

  const handleExportMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchorEl(null);
    setSelectedExportOptions([]);
  };

  const openRefundDialog = (transaction: any) => {
    setSelectedTransaction(transaction);
    setRefundReason("");
    setRefundDialogOpen(true);
  };

  const closeRefundDialog = () => {
    if (refunding) return;
    setRefundDialogOpen(false);
    setSelectedTransaction(null);
    setRefundReason("");
  };

  const showSnackbar = (message: string, type: "success" | "error" | "info") => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setSnackbarOpen(true);
  };

  const handleRefund = async () => {
    if (!selectedTransaction?.id) return;

    try {
      const { data: response } = await refundPayment({
        variables: {
          paymentId: selectedTransaction.id,
          reason: refundReason.trim() || null,
        },
      });

      const result = response?.refundPayment;
      if (!result?.success) {
        showSnackbar(result?.message || "Failed to refund payment", "error");
        return;
      }

      showSnackbar(result.message || "Refund request submitted", "success");
      closeRefundDialog();
      await refetch();
    } catch (err: any) {
      showSnackbar(err?.message || "Failed to refund payment", "error");
    }
  };

  const handleSyncRefundStatus = async (transaction: any) => {
    if (!transaction?.id) return;

    setSyncingPaymentId(transaction.id);
    try {
      const { data: response } = await syncRefundStatus({
        variables: { paymentId: transaction.id },
      });

      const result = response?.syncRefundStatus;
      showSnackbar(
        result?.message || "Refund status updated",
        result?.success ? "success" : "error"
      );
      await refetch();
    } catch (err: any) {
      showSnackbar(err?.message || "Failed to sync refund status", "error");
    } finally {
      setSyncingPaymentId(null);
    }
  };

  const handleExportExcel = (options: BillingExportOption[]) => {
    const workbook = XLSX.utils.book_new();
    const timestamp = new Date().toISOString().slice(0, 10);
    const transactionRows = filteredTransactions.map((item: any) => ({
      OrderId: item.orderId || "",
      Title: item.title || "",
      Subtitle: item.subtitle || "",
      Type: item.itemType ? item.itemType.charAt(0).toUpperCase() + item.itemType.slice(1).toLowerCase() : "",
      Amount: item.amount || 0,
      Currency: item.currency || "INR",
      Date: item.date ? new Date(item.date).toLocaleDateString() : "",
      Status: item.status || "",
      RefundStatus: item.refundStatus || "NONE",
      RefundAmount: item.refundAmount || 0,
      RefundedAt: item.refundedAt ? new Date(item.refundedAt).toLocaleString() : "",
      "Item Count": item.itemCount || 0,
    }));
    const passPurchaseRows = (billingData?.recentPassPurchases || []).map((item: any) => ({
      Name: item.name || "",
      Detail: item.detail || "",
      Time: formatDateDDMMYYYY(item.time) || item.time || "",
    }));
    const offerUsageRows = (billingData?.offerUsage || []).map((item: any) => ({
      Title: item.title || "",
      Subtitle: item.subtitle || "",
      Uses: item.uses || 0,
    }));
    const eventTicketSalesRows = (billingData?.eventTicketSales || []).map((item: any) => ({
      Name: item.name || "",
      Date: formatDateDDMMYYYY(item.date),
      Sold: item.sold || 0,
      Capacity: item.capacity || 0,
      Revenue: item.revenue || 0,
    }));

    const transactionsSheet = XLSX.utils.json_to_sheet(transactionRows);
    const passPurchasesSheet = XLSX.utils.json_to_sheet(passPurchaseRows);
    const offerUsageSheet = XLSX.utils.json_to_sheet(offerUsageRows);
    const eventTicketSalesSheet = XLSX.utils.json_to_sheet(eventTicketSalesRows);

    transactionsSheet["!cols"] = [{ wch: 22 }, { wch: 24 }, { wch: 34 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 14 }, { wch: 20 }, { wch: 12 }];
    passPurchasesSheet["!cols"] = [{ wch: 22 }, { wch: 24 }, { wch: 24 }];
    offerUsageSheet["!cols"] = [{ wch: 24 }, { wch: 28 }, { wch: 12 }];
    eventTicketSalesSheet["!cols"] = [{ wch: 24 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 14 }];

    if (options.includes("transactions") || options.includes("all")) {
      XLSX.utils.book_append_sheet(workbook, transactionsSheet, "Transactions");
    }
    if (options.includes("pass_purchases") || options.includes("all")) {
      XLSX.utils.book_append_sheet(workbook, passPurchasesSheet, "Pass Purchases");
    }
    if (options.includes("offer_usage") || options.includes("all")) {
      XLSX.utils.book_append_sheet(workbook, offerUsageSheet, "Offer Usage");
    }
    if (options.includes("event_ticket_sales") || options.includes("all")) {
      XLSX.utils.book_append_sheet(workbook, eventTicketSalesSheet, "Event Ticket Sales");
    }

    XLSX.writeFile(workbook, `billing-dashboard-${timestamp}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress sx={{ color: themestyle.colors.secondary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="error">Error loading billing data: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2.5} sx={{ pt: 2, px: { xs: 1, sm: 0 }, pb: { xs: 2, sm: 2.5 } }}>
      <Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: themestyle.colors.primary }}>
            Billing Dashboard
          </Typography>
          <Button
            variant="contained"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportMenuClick}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2.5,
              px: 2.25,
              bgcolor: "#a3d65c",
              boxShadow: "none",
              "&:hover": { bgcolor: "#86b347", boxShadow: "none" },
            }}
          >
            Export Excel
          </Button>
          <Menu
            anchorEl={exportMenuAnchorEl}
            open={Boolean(exportMenuAnchorEl)}
            onClose={handleExportMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 260,
                borderRadius: 2.5,
                border: `1px solid ${themestyle.colors.bordercolor}`,
                boxShadow: "0px 16px 40px rgba(7, 24, 51, 0.10)",
                overflow: "hidden",
              },
            }}
          >
            {BILLING_EXPORT_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleToggleExportOption(option.value)}
                sx={{ py: 1.2, fontSize: 14, fontWeight: 600 }}
              >
                <Checkbox
                  checked={selectedExportOptions.includes(option.value)}
                  sx={{
                    p: 0,
                    mr: 1.25,
                    color: "#a3d65c",
                    "&.Mui-checked": { color: "#a3d65c" },
                    "& .MuiSvgIcon-root": { fontSize: 22 },
                  }}
                />
                {option.title}
              </MenuItem>
            ))}
            <Divider />
            <Box sx={{ p: 1.25 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={() => {
                  handleExportExcel(selectedExportOptions);
                  handleExportMenuClose();
                }}
                disabled={selectedExportOptions.length === 0}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: 2,
                  bgcolor: "#a3d65c",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#86b347", boxShadow: "none" },
                }}
              >
                Download Selected
              </Button>
            </Box>
          </Menu>
        </Stack>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(4, minmax(0, 1fr))" },
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        {STATS.map((s) => (
          <CardShell key={s.label}>
            <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, "&:last-child": { pb: { xs: 1.75, sm: 2.25 } } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 800, letterSpacing: "0.02em", fontSize: 13.5 }}>
                    {s.label}
                  </Typography>
                  <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary, fontSize: { xs: 20, sm: 24 }, mt: 0.5, letterSpacing: "-0.02em" }}>
                    {s.value}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: { xs: 36, sm: 44 },
                    height: { xs: 36, sm: 44 },
                    borderRadius: "50%",
                    border: `1.5px solid ${s.color}40`,
                    bgcolor: `${s.color}08`,
                    color: s.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 8px 16px -4px ${s.color}15`,
                    "& svg": { fontSize: { xs: 16, sm: 20 } }
                  }}
                >
                  {s.icon}
                </Box>
              </Stack>
            </CardContent>
          </CardShell>
        ))}
      </Box>

      <CardShell>
        <CardContent sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
          >
            <Stack 
              direction="row" 
              alignItems="center" 
              spacing={1.5}
              sx={{ 
                overflowX: "auto", 
                pb: { xs: 0.5, sm: 0 },
                "&::-webkit-scrollbar": { display: "none" },
                msOverflowStyle: "none",
                scrollbarWidth: "none",
                width: "100%"
              }}
            >
              <Box>
              <Button
                onClick={(e) => setRangeAnchorEl(e.currentTarget)}
                variant="outlined"
                endIcon={<ExpandMoreIcon sx={{ color: "#9CA3AF" }} />}
                sx={{
                  ...filterFieldSx,
                  textTransform: "none",
                  color: themestyle.colors.primary,
                  fontWeight: 600,
                  px: 2.5,
                  minHeight: 40,
                  minWidth: { xs: 160, sm: "auto" },
                  justifyContent: "space-between",
                  "& .MuiButton-endIcon": { ml: 2 }
                }}
              >
                {range === "today" ? "Day" : range === "week" ? "Week" : range === "month" ? "Month" : range === "year" ? "Year" : range === "specific-day" ? "Specific Day" : range === "specific-month" ? "Specific Month" : "Specific Year"}
              </Button>
              <Menu
                anchorEl={rangeAnchorEl}
                open={Boolean(rangeAnchorEl)}
                onClose={() => setRangeAnchorEl(null)}
                PaperProps={{
                  sx: { mt: 1, borderRadius: 2, minWidth: 160, boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)" }
                }}
              >
                <MenuItem dense onClick={() => { setRange("today"); setRangeAnchorEl(null); }} sx={{ py: 0.5 }}>Day</MenuItem>
                <MenuItem dense onClick={() => { setRange("week"); setRangeAnchorEl(null); }} sx={{ py: 0.5 }}>Week</MenuItem>
                <MenuItem dense onClick={() => { setRange("month"); setRangeAnchorEl(null); }} sx={{ py: 0.5 }}>Month</MenuItem>
                <MenuItem dense onClick={() => { setRange("year"); setRangeAnchorEl(null); }} sx={{ py: 0.5 }}>Year</MenuItem>
                <Divider />
                <MenuItem dense onClick={() => { setRange("specific-day"); setRangeAnchorEl(null); }} sx={{ py: 0.5 }}>Specific Day</MenuItem>
                <MenuItem dense onClick={() => { setRange("specific-month"); setRangeAnchorEl(null); }} sx={{ py: 0.5 }}>Specific Month</MenuItem>
                <MenuItem dense onClick={() => { setRange("specific-year"); setRangeAnchorEl(null); }} sx={{ py: 0.5 }}>Specific Year</MenuItem>
              </Menu>
            </Box>

            {range === "specific-day" && (
              <AppDatePicker
                value={selectedDate}
                onChange={(v) => setSelectedDate(v)}
              />
            )}
            {range === "specific-month" && (
              <TextField
                type="month"
                size="small"
                value={selectedMonth}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedMonth(e.target.value)}
                sx={{ ...filterFieldSx, minWidth: 150 }}
              />
            )}
            {range === "specific-year" && (
              <TextField
                type="number"
                size="small"
                placeholder="Year"
                value={selectedYear}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedYear(e.target.value)}
                sx={{ ...filterFieldSx, minWidth: 100 }}
              />
            )}
            </Stack>
            <Box sx={{ flexGrow: 1 }} />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1.5}
            >
              <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 800, letterSpacing: "0.05em", whiteSpace: "nowrap", fontSize: 13.5 }}>
                Filter Category
              </Typography>
              <Select
                size="small"
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                sx={{
                  ...filterFieldSx,
                  minWidth: { xs: 160, sm: 200 },
                  "& .MuiSelect-select": { fontWeight: 600, color: themestyle.colors.primary, pr: 4 }
                }}
              >
                <MenuItem dense value="all" sx={{ py: 0.5 }}>All Transactions</MenuItem>
                <MenuItem dense value="pass" sx={{ py: 0.5 }}>Pass Purchases Only</MenuItem>
                <MenuItem dense value="event" sx={{ py: 0.5 }}>Event Tickets Only</MenuItem>
              </Select>
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearBillingFilters}
                disabled={!canResetBillingFilters}
                sx={{
                  minWidth: { xs: "100%", sm: 92 },
                  height: 40,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  color: canResetBillingFilters ? themestyle.colors.primary : "#9CA3AF",
                  borderColor: canResetBillingFilters ? themestyle.colors.bordercolor : "#D1D5DB",
                  alignSelf: { xs: "stretch", sm: "center" },
                }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </CardShell>

      <CardShell>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2.5, py: 2 }}
          >
            <Typography sx={{ fontWeight: 600, color: themestyle.colors.primary, fontSize: 18 }}>
              Recent Transactions
            </Typography>
            {filteredTransactions.length > TRANSACTIONS_PER_PAGE && (
              <ResponsivePagination
                count={Math.ceil(filteredTransactions.length / TRANSACTIONS_PER_PAGE)}
                page={transactionsPage}
                onChange={(val) => setTransactionsPage(val)}
              />
            )}
          </Stack>
          <Divider />
          <Box sx={{ px: { xs: 1.25, sm: 1.75 }, pb: { xs: 1.25, sm: 1.75 }, overflowX: "auto" }}>
            <Table size="small" sx={{ mt: 1, minWidth: 820 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: themestyle.colors.primary, fontWeight: 900, fontSize: 13, borderBottom: "none" }}>Name</TableCell>
                  <TableCell sx={{ color: themestyle.colors.primary, fontWeight: 900, fontSize: 13, borderBottom: "none" }}>Type</TableCell>
                  <TableCell sx={{ color: themestyle.colors.primary, fontWeight: 900, fontSize: 13, borderBottom: "none" }}>Amount</TableCell>
                  <TableCell sx={{ color: themestyle.colors.primary, fontWeight: 900, fontSize: 13, borderBottom: "none" }}>Date</TableCell>
                  <TableCell sx={{ color: themestyle.colors.primary, fontWeight: 900, fontSize: 13, borderBottom: "none" }}>Status</TableCell>
                  <TableCell sx={{ color: themestyle.colors.primary, fontWeight: 900, fontSize: 13, borderBottom: "none" }}>Refund</TableCell>
                  <TableCell sx={{ color: themestyle.colors.primary, fontWeight: 900, fontSize: 13, borderBottom: "none" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTransactions.map((t: any) => {
                  const status = statusChip(t.status);
                  return (
                    <TableRow key={t.id} sx={tableRowSx}>
                      <TableCell sx={{ ...tableBodyStrongCellSx, width: 280, minWidth: 280 }}>
                        <Box sx={{ width: 280 }}>
                          <TruncatedTitle 
                            text={t.title}
                            maxLines={2}
                            variant="body2"
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: "0.875rem", 
                              color: themestyle.colors.primary,
                              lineHeight: 1.2
                            }}
                          />
                          <TruncatedTitle 
                            text={t.subtitle}
                            maxLines={1}
                            variant="caption"
                            sx={{ 
                              color: "#94A3B8", 
                              fontWeight: 700,
                              display: "block",
                              mt: 0.25
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        <Chip 
                          label={t.itemType ? t.itemType.toUpperCase() : ""} 
                          size="small"
                          sx={{ 
                            height: 20, 
                            fontSize: "10px", 
                            fontWeight: 900, 
                            bgcolor: "#F3F4F6", 
                            color: "#6B7280",
                            borderRadius: "4px"
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tableBodyStrongCellSx, color: "#64748B" }}>₹{t.amount}</TableCell>
                      <TableCell sx={{ ...tableBodyCellSx, color: "#64748B" }}>{new Date(t.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</TableCell>
                      <TableCell sx={{ ...tableBodyCellSx, color: "#64748B" }}>
                        <Chip
                          size="small"
                          label={status.label}
                          sx={{ 
                            height: 26, 
                            fontWeight: 900, 
                            fontSize: "10px",
                            bgcolor: status.bg, 
                            color: status.fg,
                            border: `1.5px solid ${status.fg}20`,
                            borderRadius: "999px",
                            minWidth: 80
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "#64748B", fontWeight: 700 }}>
                        {t.refundStatus === "SUCCESS" && t.refundedAt
                          ? `₹${t.refundAmount || t.amount} on ${new Date(t.refundedAt).toLocaleDateString()}`
                          : t.refundStatus === "PENDING" || t.refundStatus === "ONHOLD"
                            ? "In progress"
                            : t.refundStatus === "FAILED" || t.refundStatus === "CANCELLED"
                              ? (t.refundFailureReason || "Failed")
                              : "Not refunded"}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {t.canRefund ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openRefundDialog(t)}
                              sx={{
                                textTransform: "none",
                                fontWeight: 800,
                                borderRadius: 2,
                                borderColor: "#FCA5A5",
                                color: "#B91C1C",
                                "&:hover": { borderColor: "#EF4444", bgcolor: "#FEF2F2" },
                              }}
                            >
                              Refund
                            </Button>
                          ) : t.canSyncRefund ? (
                            <Button
                              size="small"
                              variant="text"
                              disabled={syncingPaymentId === t.id}
                              onClick={() => handleSyncRefundStatus(t)}
                              sx={{
                                textTransform: "none",
                                fontWeight: 800,
                                color: t.refundStatus === "FAILED" || t.refundStatus === "CANCELLED" ? "#B91C1C" : themestyle.colors.link,
                                "&:hover": { bgcolor: t.refundStatus === "FAILED" || t.refundStatus === "CANCELLED" ? "#FEF2F2" : "#EFF6FF" },
                              }}
                            >
                              {syncingPaymentId === t.id ? "Checking..." : "Check status"}
                            </Button>
                          ) : (
                            <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 700 }}>
                              {t.refundStatus === "SUCCESS" ? "Refunded" : "Unavailable"}
                            </Typography>
                          )}
                          {t.status !== "Refunded" && t.refundStatus !== "SUCCESS" && (
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadInvoice(t.id, t.id)}
                              disabled={downloadingId === t.id}
                              sx={{ 
                                color: themestyle.colors.secondary,
                                "&:hover": { bgcolor: "rgba(163, 214, 92, 0.08)" }
                              }}
                            >
                              {downloadingId === t.id ? (
                                <CircularProgress size={18} thickness={6} sx={{ color: themestyle.colors.secondary }} />
                              ) : (
                                <DownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredTransactions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                      <Typography variant="body2" sx={{ color: "#9CA3AF", fontWeight: 600 }}>No transactions found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </CardShell>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <CardShell>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontWeight: 600, color: themestyle.colors.primary, fontSize: 18 }}>
                  Recent Pass Purchases
                </Typography>
                {passPurchases.length > PASS_PURCHASES_PER_PAGE && (
                  <ResponsivePagination
                    count={Math.ceil(passPurchases.length / PASS_PURCHASES_PER_PAGE)}
                    page={passPurchasesPage}
                    onChange={(val) => setPassPurchasesPage(val)}
                  />
                )}
              </Stack>
            </Box>
            <Divider />
            <Stack spacing={0} sx={{ px: 2.5, py: 1.5 }}>
              {paginatedPassPurchases.map((p: any, idx: number) => (
                <Box key={`${p.name}-${idx}`} sx={{ py: 1.5 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                  >
                    <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar 
                        sx={{ 
                          width: 36, 
                          height: 36, 
                          bgcolor: "#F3F4F6", 
                          color: "#6B7280", 
                          fontSize: 14, 
                          fontWeight: 800 
                        }}
                      >
                        {p.name.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: themestyle.colors.primary, fontSize: "0.875rem" }} noWrap>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 700 }}>
                          {p.detail}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 800, whiteSpace: "nowrap" }}>
                      {formatDateDDMMYYYY(p.time) || p.time}
                    </Typography>
                  </Stack>
                  {idx !== paginatedPassPurchases.length - 1 ? <Divider sx={{ mt: 1.5, borderColor: "#F3F4F6" }} /> : null}
                </Box>
              ))}
              {passPurchases.length === 0 && (
                <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
                  <Typography variant="body2" sx={{ textAlign: "center", color: "#9CA3AF", fontWeight: 600 }}>No recent pass purchases</Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </CardShell>

        <CardShell>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontWeight: 600, color: themestyle.colors.primary, fontSize: 18 }}>
                  Offer's Used
                </Typography>
                {offerUsage.length > OFFER_USAGE_PER_PAGE && (
                  <ResponsivePagination
                    count={Math.ceil(offerUsage.length / OFFER_USAGE_PER_PAGE)}
                    page={offerUsagePage}
                    onChange={(val) => setOfferUsagePage(val)}
                  />
                )}
              </Stack>
            </Box>
            <Divider />
            <Stack spacing={0} sx={{ px: 2.5, py: 1.5 }}>
              {paginatedOfferUsage.map((o: any, idx: number) => (
                <Box key={`${o.title}-${idx}`} sx={{ py: 1.5 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                      <Box sx={{ 
                        width: 36, 
                        height: 36, 
                        borderRadius: "50%", 
                        border: "1.5px solid #F59E0B40", 
                        bgcolor: "#F59E0B08", 
                        color: "#F59E0B", 
                        display: "grid", 
                        placeItems: "center", 
                        flex: "0 0 auto"
                      }}>
                        <PercentIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: themestyle.colors.primary, fontSize: "0.875rem" }} noWrap>{o.title}</Typography>
                        <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 700 }}>{o.subtitle}</Typography>
                      </Box>
                    </Stack>
                    <Chip 
                      label={formatUsageLabel(o.uses)} 
                      size="small"
                      sx={{ 
                        bgcolor: "#FDF2F8", 
                        color: "#DB2777", 
                        fontWeight: 900, 
                        fontSize: "11px",
                        height: 24,
                        border: "1px solid #FCE7F3"
                      }}
                    />
                  </Stack>
                  {idx !== paginatedOfferUsage.length - 1 ? <Divider sx={{ mt: 1.5, borderColor: "#F3F4F6" }} /> : null}
                </Box>
              ))}
              {offerUsage.length === 0 && (
                <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
                  <Typography variant="body2" sx={{ textAlign: "center", color: "#9CA3AF", fontWeight: 600 }}>No offer usage data</Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </CardShell>
      </Box>

      <CardShell>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontWeight: 600, color: themestyle.colors.primary, fontSize: 18 }}>
                Event Ticket Sales
              </Typography>
              {eventTicketSales.length > EVENT_SALES_PER_PAGE && (
                <ResponsivePagination
                  count={Math.ceil(eventTicketSales.length / EVENT_SALES_PER_PAGE)}
                  page={eventSalesPage}
                  onChange={(val) => setEventSalesPage(val)}
                />
              )}
            </Stack>
          </Box>
          <Divider />
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 760, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  {["Event Name", "Event Date", "Tickets Sold", "Revenue"].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        ...tableHeadCellSx,
                        color: themestyle.colors.primary,
                        width: "25%",
                      }}
                      align={label === "Event Name" ? "left" : "center"}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedEventSales.map((r: any) => (
                  <TableRow key={r.name} sx={tableRowSx}>
                    <TableCell sx={{ ...tableBodyStrongCellSx, width: "25%" }} align="left">
                      <TruncatedTitle 
                        text={r.name} 
                        variant="body2" 
                        maxLines={1}
                        sx={{ fontSize: "inherit", fontWeight: "inherit", color: "#64748B" }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...tableBodyCellSx, color: "#64748B" }} align="center">{formatDateDDMMYYYY(r.date)}</TableCell>
                    <TableCell sx={{ ...tableBodyStrongCellSx, color: "#64748B" }} align="center">{r.sold}</TableCell>
                    <TableCell sx={{ ...tableBodyStrongCellSx, color: "#64748B" }} align="center">₹{formatCurrencyTwoDecimals(r.revenue)}</TableCell>
                  </TableRow>
                ))}
                {eventTicketSales.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                      <Typography variant="body2" color="#9CA3AF" fontWeight={600}>No event sales data</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </CardShell>

      <Dialog
        open={refundDialogOpen}
        onClose={closeRefundDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${themestyle.colors.bordercolor}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: themestyle.colors.primary }}>
          Refund payment
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: "1px solid #E2E8F0",
                bgcolor: "#F8FAFC",
              }}
            >
              <Typography sx={{ fontWeight: 800, color: themestyle.colors.primary }}>
                {selectedTransaction?.title || "Selected payment"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, color: "#64748B" }}>
                Order: {selectedTransaction?.orderId || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748B" }}>
                Amount to refund: ₹{selectedTransaction?.amount || 0}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#64748B" }}>
              This submits a full refund request to Cashfree for the selected transaction.
            </Typography>

            <TextField
              label="Refund reason"
              placeholder="Optional note for audit trail"
              fullWidth
              multiline
              minRows={3}
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={closeRefundDialog}
            disabled={refunding}
            sx={{ textTransform: "none", fontWeight: 700, color: "#64748B" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRefund}
            disabled={refunding}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2,
              bgcolor: "#DC2626",
              boxShadow: "none",
              "&:hover": { bgcolor: "#B91C1C", boxShadow: "none" },
            }}
          >
            {refunding ? "Refunding..." : "Confirm refund"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Stack>
  );
}
const filterFieldSx = {
  bgcolor: "#F9FAFB",
  borderRadius: 2.5,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D1D5DB",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#A3D65C",
  },
};

const tableHeadCellSx = {
  color: "#9CA3AF",
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: "0.02em",
  borderBottom: "1px solid #F3F4F6",
  whiteSpace: "nowrap",
};

const tableBodyCellSx = {
  color: "#6B7280",
  fontWeight: 600,
  borderBottom: "1px solid #F3F4F6",
  py: 2,
};

const tableBodyStrongCellSx = {
  ...tableBodyCellSx,
  color: themestyle.colors.primary,
  fontWeight: 700,
};

const tableRowSx = {
  "&:last-child td": {
    borderBottom: "none",
  },
};

const panelSx = {
  border: `1px solid ${themestyle.colors.bordercolor}`,
  borderRadius: "18px",
  boxShadow: "0px 16px 40px rgba(7, 24, 51, 0.06)",
};
